/** Common ISO currencies for partner trip finance. */
export const PARTNER_FINANCE_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'AED', 'CHF', 'CAD', 'AUD', 'JPY', 'CNY', 'SGD', 'HKD', 'INR', 'ZAR', 'MXN', 'BRL',
];

const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AED: 'د.إ',
  CHF: 'CHF',
  CAD: 'C$',
  AUD: 'A$',
  JPY: '¥',
  CNY: '¥',
  SGD: 'S$',
  HKD: 'HK$',
  INR: '₹',
  ZAR: 'R',
  MXN: 'MX$',
  BRL: 'R$',
};

export function currencySymbol(code) {
  const c = (code || 'USD').toUpperCase();
  return CURRENCY_SYMBOLS[c] || `${c} `;
}

/**
 * Format like "USD $28,500" (code + localized amount with symbol).
 */
export function formatMoney(amount, currency = 'USD', { showCode = true } = {}) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const code = (currency || 'USD').toUpperCase();
  const n = Number(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);
  const symbol = currencySymbol(code);
  const withSymbol = symbol.length > 1 && !symbol.endsWith('$') && symbol !== '€' && symbol !== '£' && symbol !== '¥' && symbol !== '₹'
    ? `${symbol} ${formatted}`
    : `${symbol}${formatted}`;
  return showCode ? `${code} ${withSymbol}` : withSymbol;
}

export function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * not_invoiced | deposit_due | partially_paid | paid | overdue
 */
export function computePaymentStatus({ tripValue, clientPaid, paymentDueDate }, today = todayYmd()) {
  if (tripValue == null || tripValue === '' || Number.isNaN(Number(tripValue))) return 'not_invoiced';
  const value = Number(tripValue);
  const paid = Number(clientPaid) || 0;
  if (value <= 0 && paid <= 0) return 'not_invoiced';
  const balance = Math.round((value - paid) * 100) / 100;
  if (balance <= 0) return 'paid';
  const due = paymentDueDate ? String(paymentDueDate).slice(0, 10) : null;
  if (due && due < today) return 'overdue';
  if (paid > 0) return 'partially_paid';
  return 'deposit_due';
}

export function financeToForm(finance) {
  return {
    tripValue: finance?.tripValue != null ? String(finance.tripValue) : '',
    clientPaid: finance?.clientPaid != null ? String(finance.clientPaid) : '',
    paymentDueDate: finance?.paymentDueDate || '',
    commissionExpected: finance?.commissionExpected != null ? String(finance.commissionExpected) : '',
    commissionReceived: finance?.commissionReceived != null ? String(finance.commissionReceived) : '',
    currency: finance?.currency || 'USD',
  };
}

/**
 * @param {'full'|'member'|'commission'} [mode]
 */
export function financeFormToPayload(form, mode = 'full') {
  if (mode === 'commission') {
    return {
      commissionExpected: form.commissionExpected === '' ? null : Number(form.commissionExpected),
      commissionReceived: form.commissionReceived === '' ? 0 : Number(form.commissionReceived),
      currency: form.currency || 'USD',
    };
  }
  const payload = {
    tripValue: form.tripValue === '' ? null : Number(form.tripValue),
    paymentDueDate: form.paymentDueDate || null,
    currency: form.currency || 'USD',
  };
  // Only send manual clientPaid when not driven by payment history.
  if (!form.paidFromPayments) {
    payload.clientPaid = form.clientPaid === '' ? 0 : Number(form.clientPaid);
  }
  if (mode === 'full') {
    payload.commissionExpected = form.commissionExpected === '' ? null : Number(form.commissionExpected);
    payload.commissionReceived = form.commissionReceived === '' ? 0 : Number(form.commissionReceived);
  }
  return payload;
}

export function balanceDueFromForm(form) {
  const value = form.tripValue === '' ? null : Number(form.tripValue);
  const paid = form.clientPaid === '' ? 0 : Number(form.clientPaid);
  if (value == null || Number.isNaN(value) || Number.isNaN(paid)) return null;
  return Math.max(0, Math.round((value - paid) * 100) / 100);
}

export function rollupMemberFinance(formsOrFinanceList = []) {
  let total = 0;
  let paid = 0;
  let hasValue = false;
  for (const item of formsOrFinanceList) {
    const valueRaw = item?.tripValue;
    const paidRaw = item?.clientPaid;
    const value = valueRaw === '' || valueRaw == null ? null : Number(valueRaw);
    const paidAmt = paidRaw === '' || paidRaw == null ? 0 : Number(paidRaw);
    if (value != null && !Number.isNaN(value)) {
      hasValue = true;
      total += value;
    }
    if (!Number.isNaN(paidAmt)) paid += paidAmt;
  }
  const outstanding = Math.max(0, Math.round((total - paid) * 100) / 100);
  return {
    total: hasValue ? Math.round(total * 100) / 100 : null,
    paid: Math.round(paid * 100) / 100,
    outstanding: hasValue ? outstanding : null,
  };
}

/** @deprecated use computePaymentStatus */
export function paymentStatusFromForm(form) {
  return computePaymentStatus({
    tripValue: form.tripValue === '' ? null : form.tripValue,
    clientPaid: form.clientPaid,
    paymentDueDate: form.paymentDueDate,
  });
}
